import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "profile-header-content" }
const _hoisted_2 = { class: "profile-header-top" }
const _hoisted_3 = { class: "profile-header-actions" }
const _hoisted_4 = {
  class: "drag-handle",
  title: "Drag to move"
}
const _hoisted_5 = { class: "profile-body" }

import { ref, computed, watch, onBeforeUnmount } from "vue";
import { Icon } from "@iconify/vue";
import CompanyProfileTab from "@/components/bucket/company/CompanyProfileTab.vue";
import Unipile from "@/services/unipile/unipile";
import CompanyService from "@/services/company/company";

interface Props {
  show: boolean;
  selectedBucket?: {
    contact?: {
      company?: {
        linkedInUrl?: string;
        name?: string;
        companyId?: string;
        logoUrl?: string | null;
      };
    };
  } | null;
  linkedInChannel?: {
    accountId?: string;
    status?: string;
  } | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CompanyProfileViewerModal',
  props: {
    show: { type: Boolean, default: false },
    selectedBucket: { default: null },
    linkedInChannel: { default: null }
  },
  emits: ["close", "logo-saved"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Dragging state
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

// Profile state
const companyProfile = ref<any>(null);
const companyProfileLoading = ref(false);
const companyProfileError = ref<string | null>(null);
const companyProfileCache = ref<Record<string, any>>({});

// Dynamic positioning for drag functionality
const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};

  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
    width: "28%",
    minWidth: "350px",
    maxWidth: "500px",
    minHeight: "60vh",
    maxHeight: "85vh",
    borderRadius: "12px !important",
    zIndex: "10060",
  };
});

// Transform company profile data
const transformedProfile = computed(() => {
  if (!companyProfile.value) return null;

  const profile = companyProfile.value;
  return {
    companyName: profile.name || "",
    description: profile.description || "",
    industry: profile.industry || "",
    website: profile.website || "",
    companyType: profile.company_type || "",
    employeeCount: profile.employee_count || null,
    headquarters: profile.headquarters || "",
    specialties: profile.specialties || [],
    foundedYear: profile.founded_year || null,
    logoUrl: profile.logo_large || profile.logo || "",
    coverImageUrl: profile.cover_image_url || "",
    followersCount: profile.followers_count || null,
  };
});

// Drag handlers
const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [
    ".btn-close-profile",
    ".profile-body",
    ".company-profile-tab",
    ".profile-card",
    "a",
    "button",
    "input",
    "textarea",
  ];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 480) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  const dragEvents = [
    ["mousemove", handleDrag, { passive: false }],
    ["mouseup", stopDrag],
    ["touchmove", handleDrag, { passive: false }],
    ["touchend", stopDrag],
  ] as const;

  dragEvents.forEach(([event, handler, options]) => {
    document.addEventListener(event, handler as any, options);
  });
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;

  const dragEvents = ["mousemove", "mouseup", "touchmove", "touchend"];
  const handlers = [handleDrag, stopDrag, handleDrag, stopDrag];

  dragEvents.forEach((event, index) => {
    document.removeEventListener(event, handlers[index] as any);
  });
};

// Helper functions
const isChannelHealthyStatus = (status?: string): boolean => {
  if (!status) return false;
  const s = String(status).toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(s);
};

const extractCompanyIdentifierFromUrl = (url?: string): string | null => {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    // Match company URLs: /company/company-name
    const companyMatch = path.match(/\/company\/([^/]+)/i);
    if (companyMatch?.[1]) return decodeURIComponent(companyMatch[1]);

    // Fallback: try to extract from path
    const parts = path.split("/").filter(Boolean);
    if (parts.length === 0) return null;
    return decodeURIComponent(parts[parts.length - 1]);
  } catch (error) {
    console.error("Failed to parse company LinkedIn URL:", error);
    return null;
  }
};

// Load company profile
const loadCompanyProfile = async () => {
  const companyLinkedInUrl = props.selectedBucket?.contact?.company?.linkedInUrl;
  if (!companyLinkedInUrl) {
    companyProfileError.value = "Please add a LinkedIn URL to the company first.";
    companyProfile.value = null;
    return;
  }

  if (!props.linkedInChannel?.accountId) {
    companyProfileError.value = "LinkedIn channel is not connected.";
    companyProfile.value = null;
    return;
  }

  if (!isChannelHealthyStatus(props.linkedInChannel?.status)) {
    companyProfileError.value = "LinkedIn channel is disconnected. Please reconnect first.";
    companyProfile.value = null;
    return;
  }

  const identifier = extractCompanyIdentifierFromUrl(companyLinkedInUrl);
  if (!identifier) {
    companyProfileError.value = "Invalid company LinkedIn URL. Please update the company.";
    companyProfile.value = null;
    return;
  }

  const cacheKey = `${identifier}-${props.linkedInChannel.accountId}`;
  if (companyProfileCache.value[cacheKey]) {
    companyProfile.value = companyProfileCache.value[cacheKey];
    companyProfileError.value = null;
    return;
  }

  try {
    companyProfileLoading.value = true;
    companyProfileError.value = null;

    const response = await Unipile.getCompanyProfile(identifier, {
      account_id: props.linkedInChannel.accountId,
    });

    const profileData = response?.data?.data;
    if (!profileData) {
      companyProfileError.value = "Company profile data not available.";
      companyProfile.value = null;
      return;
    }

    companyProfileCache.value[cacheKey] = profileData;
    companyProfile.value = profileData;
    companyProfileError.value = null;

    const companyId = props.selectedBucket?.contact?.company?.companyId;
    const alreadyHasLogo = !!props.selectedBucket?.contact?.company?.logoUrl;
    const logoUrl = profileData.logo_large || profileData.logo;
    if (companyId && !alreadyHasLogo && logoUrl) {
      saveCompanyLogo(companyId, logoUrl);
    }
  } catch (error: any) {
    console.error("Failed to load company profile:", error);
    companyProfileLoading.value = false;

    const statusCode = error?.response?.status;
    const errorData = error?.response?.data;

    const getErrorMessage = () => {
      if (errorData?.providerError?.detail) return errorData.providerError.detail;
      if (errorData?.error?.body?.detail) return errorData.error.body.detail;
      if (errorData?.errorMessage) return errorData.errorMessage;
      if (errorData?.message) return errorData.message;
      if (errorData?.providerError?.title) return errorData.providerError.title;
      if (error?.message) return error.message;
      return null;
    };

    const extractedMessage = getErrorMessage();
    let errorMessage = "Failed to load company profile.";

    switch (statusCode) {
      case 400:
        errorMessage =
          extractedMessage || "Invalid request. Please check the company LinkedIn URL.";
        break;
      case 401:
        errorMessage =
          extractedMessage ||
          "Your LinkedIn account appears to be disconnected. Please reconnect your account.";
        break;
      case 403:
        errorMessage =
          extractedMessage ||
          "This feature is not available for your subscription or account type.";
        break;
      case 404:
        errorMessage =
          extractedMessage || "Company not found. The requested company was not found.";
        break;
      case 422:
        errorMessage = extractedMessage || "Invalid account for this feature.";
        break;
      case 429:
        errorMessage = extractedMessage || "Too many requests. Please wait a moment and try again.";
        break;
      case 500:
      case 503:
      case 504:
        errorMessage =
          extractedMessage ||
          "LinkedIn service is temporarily unavailable. Please try again later.";
        break;
      default:
        errorMessage =
          extractedMessage || error?.message || "An unexpected error occurred. Please try again.";
    }

    companyProfileError.value = errorMessage;
    companyProfile.value = null;
  } finally {
    companyProfileLoading.value = false;
  }
};

const saveCompanyLogo = async (companyId: string, logoUrl: string) => {
  try {
    const response = await CompanyService.saveCompanyLogo(companyId, logoUrl);
    if (response.data.status) {
      emit("logo-saved", response.data.data.logoUrl);
    }
  } catch {
    // Silent failure — logo is non-critical
  }
};

const close = () => {
  isInitialPosition.value = true;
  modalPosition.value = { x: 0, y: 0 };
  emit("close");
};

// Watch for show prop changes
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      loadCompanyProfile();
    } else {
      companyProfileError.value = null;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "modal-fade" }, {
    default: _withCtx(() => [
      (__props.show)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["profile-modal-overlay", { 'dragging-active': !isInitialPosition.value }])
          }, [
            _createElementVNode("div", {
              ref_key: "modalRef",
              ref: modalRef,
              class: _normalizeClass(["profile-modal", { 'is-dragging': isDragging.value }]),
              style: _normalizeStyle(modalStyle.value),
              onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
              onMousedown: startDrag,
              onTouchstart: startDrag
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["profile-header", { draggable: true }])
              }, [
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("div", _hoisted_2, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:linkedin",
                      width: "16",
                      height: "16",
                      class: "header-icon"
                    }),
                    _cache[1] || (_cache[1] = _createElementVNode("h4", { class: "profile-title" }, "LinkedIn Company Profile", -1))
                  ])
                ]),
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:drag",
                      width: "16",
                      height: "16"
                    })
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "btn-close-profile",
                    onClick: close
                  }, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:close",
                      width: "18",
                      height: "18"
                    })
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(CompanyProfileTab, {
                  profile: transformedProfile.value,
                  loading: companyProfileLoading.value,
                  error: companyProfileError.value,
                  onRetry: loadCompanyProfile
                }, null, 8, ["profile", "loading", "error"])
              ])
            ], 38)
          ], 2))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})